import React, { useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field, FieldSet, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';
import {
  ProvisionSelectOption,
  ProvisionSelectSingleValue,
  provisionsToOptions,
} from '../provisions/ProvisionSelectOption';
import * as styles from './AdminSSOUserCreationSettingCard.module.scss';

const ORGANIZATION_SSO_USER_CREATION_SETTINGS_QUERY = gql(/** @complexity 5 */ `
  query organizationSsoUserCreationSettings {
    session: currentSession {
      organization {
        id
        settings {
          allowSsoUserCreation
        }
        provisions {
          id
          name
          isSsoDefault
          ...provisionsToOptions_provision
        }
      }
    }
  }
`);

const UPDATE_ORGANIZATION_MUTATION = gql(/** @complexity 4 */ `
  mutation updateOrganizationSSOCreation(
    $allowSsoUserCreation: Boolean!
    $ssoProvisionId: ID
  ) {
    organization: updateOrganization(
      settings: { allowSsoUserCreation: $allowSsoUserCreation }
      ssoProvisionId: $ssoProvisionId
    ) {
      id
      settings {
        allowSsoUserCreation
      }
    }
  }
`);

const SSO_USER_CREATION_LABELS = {
  enabled:
    'A user is created automatically for all new users who log in using SSO.',
  disabled: 'Require an invitation for new users to gain access to Slab.',
};

const TITLE = 'New users';

interface AdminSSOUserCreationSettingCardProps {
  disabled?: boolean;
  initialIsEditing?: boolean;
}

type Values = {
  allowCreation: 'enabled' | 'disabled';
  ssoProvisionId: string | null;
};

const AdminSSOUserCreationSettingCard = ({
  disabled,
  initialIsEditing,
}: AdminSSOUserCreationSettingCardProps) => {
  const [isEditing, setIsEditing] = useState(initialIsEditing);

  const client = useApolloClient();
  const { data } = useQuery(ORGANIZATION_SSO_USER_CREATION_SETTINGS_QUERY);

  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION_MUTATION);

  const organization = data?.session?.organization;

  if (!organization) {
    return null;
  }

  const { provisions, settings } = organization;
  const isSsoUserCreationAllowed = settings?.allowSsoUserCreation;
  const ssoProvision = provisions.find(({ isSsoDefault }) => isSsoDefault);
  const ssoProvisionId = ssoProvision ? ssoProvision.id : null;

  const updateIsSsoDefaultForProvision = (
    provisionId: string,
    isSsoDefault: boolean,
  ) => {
    client.cache.modify({
      id: client.cache.identify({ __typename: 'Provision', id: provisionId }),
      fields: { isSsoDefault: () => isSsoDefault },
    });
  };

  const handleSubmit = (values: Values) =>
    updateOrganization({
      variables: {
        allowSsoUserCreation: values.allowCreation === 'enabled',
        ssoProvisionId: values.ssoProvisionId,
      },
    }).then(({ data: mutationData }) => {
      if (mutationData?.organization) {
        if (ssoProvisionId) {
          updateIsSsoDefaultForProvision(ssoProvisionId, false);
        }
        if (values.ssoProvisionId) {
          updateIsSsoDefaultForProvision(values.ssoProvisionId, true);
        }
      }
      setIsEditing(false);
    });

  if (isEditing) {
    return (
      <SettingCard
        description={
          <Form
            initialValues={{
              allowCreation: isSsoUserCreationAllowed ? 'enabled' : 'disabled',
              ssoProvisionId,
            }}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue }) => (
              <>
                <FieldSet legend={TITLE}>
                  {(['disabled', 'enabled'] as const).map((value) => (
                    <Field
                      key={value}
                      label={SSO_USER_CREATION_LABELS[value]}
                      name="allowCreation"
                      type="radio"
                      value={value}
                    />
                  ))}
                </FieldSet>
                {values.allowCreation === 'enabled' && (
                  <div className={styles.provisionsField}>
                    <Field
                      isClearable
                      isSearchable={false}
                      isDisabled={provisions.length === 0}
                      placeholder={
                        provisions.length > 0
                          ? 'No provision applied'
                          : 'No provisions exist'
                      }
                      name="ssoProvisionId"
                      onChange={(newId) =>
                        setFieldValue('ssoProvisionId', newId)
                      }
                      options={provisionsToOptions(provisions)}
                      type="select"
                      label="Provision"
                      components={{
                        Option: ProvisionSelectOption,
                        SingleValue: ProvisionSelectSingleValue,
                      }}
                    />
                  </div>
                )}
                <div className={styles.formFooter}>
                  <Submit variant="primary">Save</Submit>
                </div>
              </>
            )}
          </Form>
        }
      />
    );
  }

  const provisionDescription = ssoProvision ? (
    <>
      {' '}
      By default, they will be provisioned as <i>{ssoProvision.name}</i>.
    </>
  ) : (
    ''
  );

  const fullDescription = isSsoUserCreationAllowed ? (
    <>
      {SSO_USER_CREATION_LABELS.enabled}
      {provisionDescription}
    </>
  ) : (
    SSO_USER_CREATION_LABELS.disabled
  );
  const description = disabled
    ? 'You must have at least one SSO provider to change this setting.'
    : fullDescription;

  return (
    <SettingCard
      actions={
        <Button
          disabled={disabled}
          onClick={() => setIsEditing(true)}
          variant="secondary"
        >
          Edit
        </Button>
      }
      disabled={disabled}
      title={TITLE}
      description={description}
    />
  );
};

export default AdminSSOUserCreationSettingCard;
